import { useEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { proNavigator } from "single-spa/react-app/ProNavigator";

import industryTrackingService from "../services/tracking/trackingService";
import { useIndustryTransitionModalStore } from "../store/useIndustryTransitionModalStore";

import { useShowsIndustryTaxonomyAdminOffer } from "./useShowsIndustryTaxonomyAdminOffer";

/**
 * Opens the impact report from an email deep link (`?openImpactReport=true` on the home state), so a
 * marketing CTA can reproduce the on-page enroll button's click. Honours the same admin-offer gate the
 * button self-applies, and tags the open as email-originated for analytics.
 *
 * The open is deferred to a microtask so it lands after `SalesIntelligenceRoot`'s mount-time
 * `store.reset()` (the abstract parent's re-entry safety net) — whatever the parent/leaf mount ordering,
 * a same-tick child `open()` would otherwise be clobbered by that reset. The one-shot ref keeps it to a
 * single open and guards the deferred window against a double-fire.
 */
export const useOpenImpactReportFromRouteParam = (): void => {
    const { params } = useCurrentStateAndParams();

    const showsAdminOffer = useShowsIndustryTaxonomyAdminOffer();

    const hasOpenedRef = useRef(false);

    useEffect(() => {
        if (hasOpenedRef.current || params.openImpactReport !== "true" || !showsAdminOffer) {
            return;
        }

        hasOpenedRef.current = true;

        queueMicrotask(() => {
            industryTrackingService.trackImpactReportOpen("email");

            useIndustryTransitionModalStore.getState().open();

            // Erase on arrival so a refresh / back-nav / SI re-entry doesn't reopen it. The param is
            // `dynamic`, so this URL replace updates in place without re-entering the state.

            proNavigator.updateQueryParams({ openImpactReport: null }, { reload: false, location: "replace" });
        });
    }, [params.openImpactReport, showsAdminOffer]);
};
